import styles from "./PrevetVesselSpeedChart.module.scss"
import BigNumber from "bignumber.js"
import ReactEcharts, { EChartsInstance } from "echarts-for-react"
import {
  Dispatch,
  MutableRefObject,
  SetStateAction,
  memo,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import {
  average as findAverage,
  max as findMax,
  min as findMin,
} from "simple-statistics"
import { getSpeedChartOptions } from "../helpers/getSpeedChartOptionsNew"
import { resolveClickedLeg } from "../helpers/resolveClickedLeg"
import { useFetchVesselLegs } from "../hooks/useFetchVesselLegs"
import { PrevetSpeedGraphZoneType } from "../getSpeedChartOptionsHelpers"
import { VesselLeg } from "../hooks/usePrevetAPI"
import { useWoopraAPI } from "instrumentation/tracking/useWoopraApi"
import { VesselSegmentInputSelection } from "./PrevetMain"
import { SpeedChartStatsHeader } from "./SpeedChartStatsHeader"
import { usePrevetTabStateContext } from "../hooks/prevetContext"
import { getSpeedDiffLabel } from "../getSpeedChartOptionsHelpers"
import { PrevetZoneAvgPopover } from "./PrevetZoneAvgPopover"

export interface SpeedChartClickEvent {
  name: string
  /**
   * The raw item behind the clicked element. Only a `componentType: "series"`
   * click carries a speed data point here; markArea / markLine clicks carry that
   * marker's own definition instead, which is why `resolveClickedLeg` must be used
   * to turn a click into a leg.
   */
  data?: Partial<{
    average: string
    pos_date: string
    sog: number
  }>
  componentType?: "series" | "markArea" | "markLine" | "markPoint" | string
  /** Raw data index for series clicks, marker-local index for everything else. */
  dataIndex?: number
  /** Underlying zrender mouse event, carries `offsetX` / `offsetY`. */
  event?: { offsetX: number; offsetY: number }
  seriesName: "BALLAST" | "LADEN"
}

interface PrevetVesselSpeedChartArgs {
  vesselImo: number
  vesselName: string
  isLaden: boolean
  setIsTradingModalShown: React.Dispatch<React.SetStateAction<boolean>>
  setDateForCenterMarker: React.Dispatch<
    React.SetStateAction<undefined | string>
  >
  setChartLadenStatus: React.Dispatch<React.SetStateAction<boolean | undefined>>
  setAvgSpeed: Dispatch<SetStateAction<undefined | number>>
  setMaxSpeed: Dispatch<SetStateAction<undefined | number>>
  chartInstance: MutableRefObject<any>
  useForMinTreshold: PrevetSpeedGraphZoneType
  endDate: string
  startDate: string
  globalMin: number
  globalMax: number
  isShowStraitsCanalsChecked: boolean
  onFilteredVesselLegsChange?: (legs: VesselLeg[]) => void
  descSpeed: number
  chosenSegment: VesselSegmentInputSelection
  isShowSeaMarginChecked: boolean
}


function PrevetVesselSpeedChart({
  vesselImo,
  vesselName,
  isLaden,
  setAvgSpeed,
  setMaxSpeed,
  setIsTradingModalShown,
  setDateForCenterMarker,
  setChartLadenStatus,
  chartInstance,
  useForMinTreshold,
  endDate,
  startDate,
  globalMin,
  globalMax,
  isShowStraitsCanalsChecked,
  onFilteredVesselLegsChange,
  descSpeed,
  chosenSegment,
  isShowSeaMarginChecked,
}: PrevetVesselSpeedChartArgs) {
  const { postTrackingEvent } = useWoopraAPI()
  const { setTabStateValue, getTabStateValue, getSelectedIndex } = usePrevetTabStateContext()
  const tabIndex = getSelectedIndex()
  const { vesselLegsData } = useFetchVesselLegs({
    imo: vesselImo,
    end: endDate,
    start: startDate,
  })

  const removedIndicesMapRef = useRef<Map<number, Set<number>>>(new Map())
  const getRemovedIndices = () => {
    if (!removedIndicesMapRef.current.has(tabIndex)) {
      removedIndicesMapRef.current.set(tabIndex, new Set())
    }
    return removedIndicesMapRef.current.get(tabIndex)!
  }
  const [filteredVesselLegsTrigger, setFilteredVesselLegsTrigger] =
    useState(false)
  const brushRangeRef = useRef<[number, number] | null>(null)
  const buttonPosRef = useRef<{ left: number; top: number } | null>(null)
  const [visibleRange, setVisibleRange] = useState<[number, number]>([0, 0])
  const marginOverrideKey = isLaden
    ? "ladenMarginOverride"
    : "ballastMarginOverride"
  const marginOverride = getTabStateValue(marginOverrideKey, undefined) as
    | number
    | undefined

  const [chartStats, setChartStats] = useState<{
    min: number
    max: number
    avg: number
    apiSeaMarginFraction: number
  }>({ min: 0, max: 0, avg: 0, apiSeaMarginFraction: 0 })

  const filteredVesselLegs = useMemo(() => {
    if (!vesselLegsData) return []
    const removed = getRemovedIndices()
    const result = vesselLegsData
      .map((leg, index) => ({ ...leg, originalIndex: index }))
      .filter(
        (leg) =>
          leg.laden === isLaden &&
          (isShowStraitsCanalsChecked ||
            (leg.zone_group !== "Straits" && leg.zone_group !== "Canal")) &&
          !removed.has(leg.originalIndex!)
      )
    onFilteredVesselLegsChange?.(result)
    return result
  }, [
    vesselLegsData,
    isLaden,
    isShowStraitsCanalsChecked,
    filteredVesselLegsTrigger,
    tabIndex,
  ])

  useEffect(() => {
    if (!filteredVesselLegs || filteredVesselLegs.length < 1) {
      setAvgSpeed(undefined)
      setMaxSpeed(undefined)
      setChartStats({ min: 0, max: 0, avg: 0, apiSeaMarginFraction: 0 })
      return
    }
    const avg = findAverage(filteredVesselLegs.map((item) => item.sog))
    const max = findMax(filteredVesselLegs.map((item) => item.sog))
    const min = findMin(filteredVesselLegs.map((item) => item.sog))
    const apiSM =
      findAverage(
        filteredVesselLegs.map((item) => item.margin ?? 0)
      ) || 0
    setAvgSpeed(avg)
    setMaxSpeed(max)
    setChartStats({
      min,
      max,
      avg: BigNumber(avg).decimalPlaces(1, BigNumber.ROUND_HALF_CEIL).toNumber(),
      apiSeaMarginFraction: apiSM,
    })
  }, [filteredVesselLegs])

  const effectiveMargin = marginOverride ?? chartStats.apiSeaMarginFraction

  const handleMarginChange = (fraction: number) => {
    setTabStateValue(marginOverrideKey, fraction, undefined)
  }

  const showReset = getRemovedIndices().size > 0

  const handleRemoveSpeedData = () => {
    if (!brushRangeRef.current || !buttonPosRef.current) return
    const [from, end] = brushRangeRef.current
    const removed = getRemovedIndices()
    for (let i = from; i <= end; i++) {
      const item = filteredVesselLegs[i]
      if (item?.originalIndex !== undefined) {
        removed.add(item.originalIndex)
      }
    }
    setFilteredVesselLegsTrigger((prev) => !prev)
    postTrackingEvent({
      page: "/prevetting",
      event: "handle_remove_speed_data",
      properties: {
        vesselSegment: chosenSegment.value,
        component: "speed_chart",
      },
    })
  }

  const handleReset = () => {
    getRemovedIndices().clear()
    setFilteredVesselLegsTrigger((prev) => !prev)
  }

  useEffect(() => {
    if (!filteredVesselLegs || filteredVesselLegs.length < 1) return
    const avg = findAverage(filteredVesselLegs.map((item) => item.sog))
    const max = findMax(filteredVesselLegs.map((item) => item.sog))
    setAvgSpeed(avg)
    setMaxSpeed(max)
    setVisibleRange([0, filteredVesselLegs.length - 1])
  }, [filteredVesselLegs])
  const removeButtonId = `remove-btn-${isLaden ? "laden" : "ballast"}`

  function showRemoveButton(pos: { left: number; top: number }) {
    const chartDom = chartInstance.current?.getDom()
    if (!chartDom) return

    let btn = document.getElementById(removeButtonId)
    if (!btn) {
      btn = document.createElement("button")
      btn.id = removeButtonId
      btn.className = "PrevetRemoveButton"
      btn.innerText = "Remove"
      btn.onclick = handleRemoveSpeedData
      btn.style.position = "absolute"
      chartDom.appendChild(btn)
    }

    btn.style.display = "block"
    btn.style.left = `${pos.left}px`
    btn.style.top = `${pos.top}px`
  }

  const speedData = filteredVesselLegs
  const avg =
    filteredVesselLegs && filteredVesselLegs.length > 0
      ? findAverage(filteredVesselLegs.map((item) => item.sog))
      : 0
  const manualSmPct = marginOverride != null ? Number((marginOverride * 100).toFixed(1)) : null
  const options6M = getSpeedChartOptions({
    speedDataRaw: speedData,
    vesselName,
    ladenStatus: isLaden ? "laden" : "ballast",
    months: startDate == "6 Months" ? 6 : 12,
    showAllZones: false,
    userVesselSpeed: undefined,
    useForMinTreshold,
    globalMin,
    globalMax,
    descSpeed: descSpeed,
    avgSpeed: avg,
    showSeaMargin: isShowSeaMarginChecked,
    hideTitle: filteredVesselLegs.length > 0,
    manualSeaMarginPct: manualSmPct,
  })

  if (options6M?.series) {
    ;(options6M.series as any[]).push({
      name: "Manual Sea Margin",
      type: "line",
      xAxisIndex: 1,
      yAxisIndex: 1,
      showSymbol: false,
      silent: true,
      color: "transparent",
      tooltip: { show: false },
      markLine: {
        symbol: ["none"],
        lineStyle: { color: "red", type: "dotted", width: 2 },
        data:
          manualSmPct != null
            ? [
                {
                  yAxis: manualSmPct,
                  label: { show: false },
                },
              ]
            : [],
      },
    })
  }

  const onChartReadyCallback = (chart: any) => {
    chart.dispatchAction({
      type: "takeGlobalCursor",
      key: "brush",
      brushOption: { brushType: "lineX", brushMode: "single" },
    })

    chart.getZr().on("click", (e: any) => {
      const pointInGrid = chart.convertFromPixel({ seriesIndex: 0 }, [e.offsetX, e.offsetY])
      if (!pointInGrid) return
      const dataIndex = Math.round(pointInGrid[0])
      const option = chart.getOption()
      const dataset = option.dataset?.[0]?.source
      if (!dataset || dataIndex < 0 || dataIndex >= dataset.length) return
      if (!chart.containPixel({ seriesIndex: 0 }, [e.offsetX, e.offsetY])) return
      const dp = dataset[dataIndex]
      if (!dp?.pos_date) return
      setIsTradingModalShown(true)
      setDateForCenterMarker(dp.pos_date)
      setChartLadenStatus(isLaden)
      postTrackingEvent({
        page: "/prevetting",
        event: "click_speed_chart",
        properties: {
          speed: String(dp.sog),
          vesselSegment: chosenSegment.value,
          component: "speed_chart",
        },
      })
    })

    chart.on("brushEnd", (params: any) => {
      const { areas } = params
      if (areas.length > 0) {
        const [from, end] = areas[0].coordRanges[0]
        const pointCoord = chart.convertToPixel({ xAxisIndex: 0 }, end)
        brushRangeRef.current = [from, end]
        buttonPosRef.current = { left: pointCoord, top: 50 }
        showRemoveButton(buttonPosRef.current)

        const currentOption = chart.getOption()
        const updatedSeries = [...currentOption.series]
        updatedSeries[0] = {
          ...updatedSeries[0],
          markArea: {
            silent: false,
            z: 99,
            itemStyle: {
              color: isLaden ? "rgba(255, 0, 0, 0.2)" : "rgba(4, 75, 240, 0.2)",
              opacity: 0.4,
            },
            data: [[{ xAxis: from }, { xAxis: end }]],
          },
        }
        chart.setOption({ series: updatedSeries }, false)
        chart.dispatchAction({ type: "brush", areas: [] })
      }
      postTrackingEvent({
        page: "/prevetting",
        event: "select_region",
        properties: {
          component: "speed_chart",
          vesselSegment: chosenSegment.value,
        },
      })
    })
  }

  if (!vesselLegsData)
    return <div>Loading {isLaden ? "Laden" : "Ballast"} Speed Data...</div>

  const openTradeMap = (selectedLeg: VesselLeg, component: string) => {
    setIsTradingModalShown(true)
    setDateForCenterMarker(selectedLeg.pos_date)
    setChartLadenStatus(isLaden)
    postTrackingEvent({
      page: "/prevetting",
      event: "click_speed_chart",
      properties: {
        speed: String(selectedLeg.sog),
        vesselSegment: chosenSegment.value,
        component,
      },
    })
  }

  const zoneAvgPopover = (
    <PrevetZoneAvgPopover
      speedData={speedData}
      vesselName={vesselName}
      visibleStartIndex={visibleRange[0]}
      visibleEndIndex={visibleRange[1]}
      onSelectLeg={(leg) => openTradeMap(leg, "nearby_vessels_popover")}
    />
  )

  return (
    <div className={styles.chart}>
      <div className={styles.header}>
        {filteredVesselLegs.length > 0 ? (
          <SpeedChartStatsHeader
            isLaden={isLaden}
            months={startDate == "6 Months" ? 6 : 12}
            min={chartStats.min}
            max={chartStats.max}
            avg={chartStats.avg}
            seaMarginFraction={effectiveMargin}
            isManualOverride={marginOverride !== undefined}
            descSpeed={descSpeed}
            isShowSeaMarginChecked={isShowSeaMarginChecked}
            onMarginChange={handleMarginChange}
            onMarginReset={() =>
              setTabStateValue(marginOverrideKey, undefined, undefined)
            }
            statsExtra={zoneAvgPopover}
          />
        ) : (
          <div className={styles.popoverOnly}>{zoneAvgPopover}</div>
        )}
      </div>

      <div className={styles.plot}>
        {showReset && (
          <button
            type="button"
            className={styles.reset}
            onClick={handleReset}
          >
            <span className="PrevetClearSpan ti-back-right" aria-hidden="true"></span>
            <span className="PrevetResetHover">Reset</span>
          </button>
        )}
        <ReactEcharts
          style={{ height: "352px", width: "100%" }}
          className="PrevetVesselSpeedChart"
          onChartReady={(chart: EChartsInstance) => {
            chartInstance.current = chart
            onChartReadyCallback(chart)
          }}
          option={options6M}
          onEvents={{
            click: (event: SpeedChartClickEvent, echart: EChartsInstance) => {
              const selectedLeg = resolveClickedLeg(event, echart, speedData)
              if (!selectedLeg) return
              openTradeMap(selectedLeg, "speed_chart")
            },
            dataZoom: (event: any, echart: EChartsInstance) => {
              const opts = echart.getOption()
              const startIndex = opts.dataZoom[0].startValue
              const endIndex = opts.dataZoom[0].endValue

              setVisibleRange([startIndex, endIndex])
              const sliced = speedData.slice(startIndex, endIndex + 1)
              const zoomMin = findMin(sliced.map((item) => item.sog))
              const zoomMax = findMax(sliced.map((item) => item.sog))
              const zoomAvg = BigNumber(
                findAverage(sliced.map((item) => item.sog))
              )
                .decimalPlaces(1, BigNumber.ROUND_HALF_CEIL)
                .toNumber()
              const zoomApiSM =
                findAverage(sliced.map((item) => item.margin ?? 0)) || 0

              setAvgSpeed(zoomAvg)
              setChartStats({
                min: zoomMin,
                max: zoomMax,
                avg: zoomAvg,
                apiSeaMarginFraction: zoomApiSM,
              })
            },
          }}
        />
      </div>
    </div>
  )
}

export const MemoPrevetVesselSpeedChart = memo(PrevetVesselSpeedChart)
